// User-facing message rendered in DriverSelectorCell's dispatch warning dialog
// when needsDropReleaseWarning() returns true.
export const DROP_RELEASE_WARNING_MESSAGE = 'Move has not been drop released.';

/**
 * Checks whether a driver assignment should trigger a "not drop released" warning.
 *
 * Conditions (all must be true):
 *   1. At least one leg in the dispatch group is beyond the delivery leg
 *   2. The order is a drop (isDrop === 'Y')
 *   3. The order has NOT been drop released (dropReleaseDatetime is empty/null/zero)
 */
export function needsDropReleaseWarning(params: {
  isDrop?: string;
  deliveryLeg?: number;
  dropReleaseDatetime?: string;
  dispatchGroupLegnums: number[];
}): boolean {
  const { isDrop, deliveryLeg, dropReleaseDatetime, dispatchGroupLegnums } = params;

  // Must be a drop order
  if (isDrop !== 'Y') return false;

  // Must have a delivery leg to compare against
  if (deliveryLeg == null) return false;

  // At least one leg in the dispatch group must be past delivery
  const hasPastDeliveryLeg = dispatchGroupLegnums.some((ln) => ln > deliveryLeg);
  if (!hasPastDeliveryLeg) return false;

  // Must NOT be drop released
  if (
    dropReleaseDatetime &&
    dropReleaseDatetime !== '' &&
    dropReleaseDatetime !== '0' &&
    !dropReleaseDatetime.startsWith('0000-00-00')
  ) {
    return false;
  }

  return true;
}

/**
 * Resolve the delivery stop's ON-SCREEN leg number for the drop-release check.
 *
 * The order's saved `deliveryleg` goes stale the moment a stop is inserted or
 * removed ahead of the delivery: the legs are renumbered on screen but the
 * saved number is not. After a chassis split in front of the pickup, the
 * delivery is leg 2 on screen while the saved value still says 1, so the
 * loaded leg itself looked "past delivery" and raised "Move has not been drop
 * released" on the wrong leg. Mirrors the save payload's resolution
 * (legSavePayload.ts: first leg whose legAction2 is DELIVERY). Falls back to
 * the saved number when no DELIVERY stop is on screen mid-edit, so the
 * warning never gets quieter than it was.
 */
export function resolveDeliveryLegnum(
  legs: ReadonlyArray<{ legnum: number; legAction2?: string | null }>,
  savedDeliveryLeg?: number,
): number | undefined {
  const onScreen = legs.find((l) => l.legAction2 === 'DELIVERY');
  return onScreen?.legnum ?? savedDeliveryLeg;
}
